<template>
  <div class="station-search-overlay">
    <!-- 検索モード切り替えタブ -->
    <div class="search-mode-tabs">
      <button
        class="tab-button"
        :class="{ active: searchMode === 'number' }"
        @click="switchMode('number')"
        :disabled="loading"
      >
        <i class="bi bi-geo-alt-fill"></i>
        集積所検索
      </button>
      <button
        class="tab-button"
        :class="{ active: searchMode === 'address' }"
        @click="switchMode('address')"
        :disabled="loading"
      >
        <i class="bi bi-house-fill"></i>
        住所検索
      </button>
    </div>

    <!-- 検索ボックス -->
    <div class="search-box-container">
      <SearchInputBox
        v-model="searchQuery"
        :placeholder="currentPlaceholder"
        :disabled="loading"
        @search="handleSearch"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import SearchInputBox from '@/components/molecules/SearchInputBox.vue'

const props = defineProps({
  /**
   * ローディング状態
   */
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['search'])

const searchQuery = ref('')
const searchMode = ref('number') // 'number' | 'address'

/**
 * 現在の検索モードに応じたプレースホルダーテキスト
 */
const currentPlaceholder = computed(() => {
  if (props.loading) {
    return '地図読み込み中...'
  }
  return searchMode.value === 'number' 
    ? '集積所番号を入力してください'
    : '住所を入力してください（例: 東京都千代田区）'
})

/**
 * 検索モードを切り替える
 */
const switchMode = (mode) => {
  searchMode.value = mode
  // 検索クエリをクリア（オプション）
  searchQuery.value = ''
}

/**
 * 検索実行
 * 検索モード情報を含めて親コンポーネントに通知
 */
const handleSearch = (queryValue) => {
  // SearchInputBoxからのイベントの場合はqueryValueが渡される
  // 直接呼び出しの場合はundefinedなのでsearchQueryを使用
  const query = queryValue !== undefined ? queryValue : searchQuery.value
  
  emit('search', {
    query: query,
    mode: searchMode.value
  })
}
</script>

<style scoped>
.station-search-overlay {
  position: absolute;
  top: 15px;
  left: 300px; /* サイドバーとの距離 */
  z-index: 1001; /* 地図より上に表示 */
  width: 320px; /* 幅を少し広めに */
}

/* 検索モード切り替えタブ */
.search-mode-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  background: transparent;
}

.tab-button {
  flex: 1;
  padding: 3px 12px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(222, 226, 230, 0.6);
  border-radius: 4px 4px 0 0;
  color: #495057;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  position: relative;
  backdrop-filter: blur(10px);
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.8), 0 0 4px rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.tab-button i {
  font-size: 16px;
}

.tab-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(0, 123, 255, 0.8);
  color: #007bff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.2);
  text-shadow: 0 0 3px rgba(255, 255, 255, 1), 0 0 6px rgba(255, 255, 255, 0.6);
}

.tab-button.active {
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(0, 123, 255, 0.9);
  color: #007bff;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
  text-shadow: 0 0 3px rgba(255, 255, 255, 1), 0 0 6px rgba(255, 255, 255, 0.6);
}


.tab-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  transform: none;
}

.search-box-container {
  display: flex;
  justify-content: flex-start;
}

.search-box-container > * {
  width: 100%;
}

/* 検索ボックスのカスタムスタイル強化 */
.search-box-container :deep(.input-group) {
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}

.search-box-container :deep(.form-control) {
  border: 2px solid rgba(233, 236, 239, 0.6);
  border-right: none;
  padding: 10px 14px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  color: #333;
}

.search-box-container :deep(.form-control:focus) {
  border-color: #007bff;
  box-shadow: none;
}

.search-box-container :deep(.search-button) {
  border: 2px solid rgba(233, 236, 239, 0.6);
  border-left: none;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.8) 0%, rgba(0, 86, 179, 0.8) 100%);
  color: white;
  padding: 0 16px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.search-box-container :deep(.search-button:hover:not(:disabled)) {
  background: linear-gradient(135deg, rgba(0, 86, 179, 0.8) 0%, rgba(0, 64, 133, 0.8) 100%);
  transform: scale(1.05);
}

.search-box-container :deep(.search-button:disabled) {
  background: #e9ecef;
  border-color: #e9ecef;
  color: #adb5bd;
  transform: none;
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
